import { View } from "@react-pdf/renderer";
import {
  formatDateToDDMMYYYY,
  formatTimeFromISO,
} from "../../../../../helpers/functions";
import { stripHtmlToPlainText } from "../../../../../helpers/interventionHtml";
import { COLORS } from "../../../../forms/pdf-components/PdfStyles";
import { PdfH2, PdfSubHeading } from "../../../../forms/pdf-components/PdfText";
import {
  PdfRow,
  PdfCol,
  PdfSection,
  PdfVerticalDivider,
} from "../../../../forms/pdf-components/PdfLayout";
import { PdfDataField } from "../../../../forms/pdf-components/PdfDataField";
import { PdfCheckItem } from "../../../../forms/pdf-components/PdfCheckItem";

const LAB_REASONS_OPTIONS = [
  {
    value: "Monitor Drug Therapy / Effectiveness of Therapy",
    label: "Monitor Drug Therapy / Effectiveness of Therapy",
  },
  {
    value: "Monitor Drug Interactions / Levels",
    label: "Monitor Drug Interactions / Levels",
  },
  {
    value: "Monitor Disease / Chronic Conditions",
    label: "Monitor Disease / Chronic Conditions",
  },
  {
    value: "Determine Risk Status for Disease / Conditions",
    label: "Determine Risk Status for Disease / Conditions",
  },
];

/**
 * PdfLabInformation
 *
 * Props:
 *   pharmacistFollowUp — { laboratoryRecommendations, suggestedLabDateTime,
 *                          reasonsForLab, pharmacistRecommendations }
 */
export const PdfLabInformation = ({ pharmacistFollowUp = {} }) => {
  const {
    laboratoryRecommendations = "",
    suggestedLabDateTime = "",
    reasonsForLab = [],
    pharmacistRecommendations = "",
  } = pharmacistFollowUp || {};

  const validDate =
    suggestedLabDateTime != null &&
    suggestedLabDateTime !== "" &&
    !Number.isNaN(new Date(suggestedLabDateTime).getTime());

  const suggestedLabDateDisplay = validDate
    ? formatDateToDDMMYYYY(suggestedLabDateTime)
    : "";
  const suggestedLabTimeDisplay = formatTimeFromISO(
    suggestedLabDateTime != null ? String(suggestedLabDateTime) : ""
  );

  const hasLabRec = !!laboratoryRecommendations?.toString?.().trim();
  const hasDateOrTime =
    !!suggestedLabDateDisplay?.toString?.().trim() ||
    !!suggestedLabTimeDisplay?.toString?.().trim();
  const hasReasons =
    Array.isArray(reasonsForLab) && reasonsForLab.length > 0;

  const labSuggestionsText = !!pharmacistRecommendations?.toString?.().trim()
    ? stripHtmlToPlainText(pharmacistRecommendations || "")
    : "Not Applicable at this time";

  const hasAnyLabValues = hasLabRec || hasDateOrTime || hasReasons;

  if (!hasAnyLabValues && !pharmacistRecommendations?.toString?.().trim()) {
    return (
      <PdfSection>
        <PdfH2>Lab Information - No lab suggestions provided</PdfH2>
      </PdfSection>
    );
  }

  return (
    <PdfSection>
      <PdfH2 style={{ marginBottom: 4 }}>Lab Information</PdfH2>

      {/* Lab Recommendations + Suggested Date/Time row */}
      {(hasLabRec || hasDateOrTime) && (
        <PdfRow style={{ marginBottom: 4 }}>
          {hasLabRec && (
            <PdfCol>
              <View
                style={{
                  borderWidth: 1,
                  borderColor: COLORS.border,
                  backgroundColor: COLORS.bg,
                  padding: 5,
                  minHeight: 40,
                }}
              >
                <PdfDataField
                  label="Laboratory Recommendations:"
                  value={laboratoryRecommendations}
                  vertical
                />
              </View>
            </PdfCol>
          )}

          {hasDateOrTime && (
            <PdfCol>
              <View
                style={{
                  borderWidth: 1,
                  borderColor: COLORS.border,
                  backgroundColor: COLORS.bg,
                  padding: 5,
                  minHeight: 40,
                  flexDirection: "row",
                }}
              >
                <View style={{ flex: 1 }}>
                  <PdfDataField
                    label="Suggested Lab Date:"
                    value={suggestedLabDateDisplay}
                    vertical
                  />
                </View>
                {suggestedLabTimeDisplay && (
                  <>
                    <PdfVerticalDivider />
                    <View style={{ flex: 1, paddingLeft: 4 }}>
                      <PdfDataField
                        label="Suggested Lab Time:"
                        value={suggestedLabTimeDisplay}
                        vertical
                      />
                    </View>
                  </>
                )}
              </View>
            </PdfCol>
          )}
        </PdfRow>
      )}

      {/* Reason(s) for lab */}
      {hasReasons && (
        <View style={{ marginBottom: 4 }}>
          <PdfSubHeading style={{ marginBottom: 2 }}>
            Reason(s) for lab
          </PdfSubHeading>
          <PdfRow style={{ flexWrap: "wrap" }}>
            {LAB_REASONS_OPTIONS.filter((opt) =>
              reasonsForLab.includes(opt.value)
            ).map((opt) => (
              <PdfCol key={opt.value} flex={0.5} style={{ marginBottom: 2 }}>
                <PdfCheckItem label={opt.label} checked />
              </PdfCol>
            ))}
          </PdfRow>
        </View>
      )}

      {/* Lab Suggestions — always shown */}
      <View
        style={{
          borderWidth: 1,
          borderColor: COLORS.border,
          backgroundColor: COLORS.bg,
          padding: 5,
        }}
      >
        <PdfDataField
          label="Lab Suggestions:"
          value={labSuggestionsText}
          vertical
        />
      </View>
    </PdfSection>
  );
};
